import React, { useState, useEffect, useMemo } from "react";
import { Dialog, makeStyles } from "@material-ui/core";
import { SaveBrandKitFontPresetInput } from "graphQL/mutations/brandKit";

export interface BrandFontsQueueFamily {
  baseName: string;
  variantNames: string[];
}

interface BrandFontsQueueDialogProps {
  open: boolean;
  families: BrandFontsQueueFamily[];
  onClose: () => void;
  onSave: (input: SaveBrandKitFontPresetInput) => Promise<void> | void;
  /**
   * Optional: invoked when the user clicks the footer "Cancel" button.
   * Receives the families that were still in the queue (i.e. not yet added
   * to the Brand Kit and not yet individually "Upload"-ed away). Parents
   * should use this to DELETE the already-uploaded font files from the
   * font CDN / Brand Kit assets — the per-row "Upload" button keeps them,
   * footer "Cancel" discards them. If omitted, Cancel behaves like Close.
   */
  onCancel?: (remaining: BrandFontsQueueFamily[]) => Promise<void> | void;
  /**
   * Optional title override. Defaults to "Add to Brand Fonts".
   */
  title?: string;
}

/**
 * Queue-style dialog used after a (possibly multi-family) font upload.
 * Lists every detected family as a row. Each row can be:
 *  - Added to brand fonts (inline font-name input → Save)
 *  - Cancelled / skipped (just removed from the queue)
 * When a family is added or cancelled it disappears from the list. When the
 * list empties the dialog auto-closes.
 */
const BrandFontsQueueDialog: React.FC<BrandFontsQueueDialogProps> = ({
  open,
  families,
  onClose,
  onSave,
  onCancel,
  title = "Add to Brand Fonts",
}) => {
  const classes = useStyles();

  // Dedupe families by baseName (defensive) and track visible ones locally.
  const initialIds = useMemo(() => {
    const seen = new Set<string>();
    const out: BrandFontsQueueFamily[] = [];
    families.forEach((f) => {
      if (!seen.has(f.baseName)) {
        seen.add(f.baseName);
        out.push(f);
      }
    });
    return out;
  }, [families]);

  const [queue, setQueue] = useState<BrandFontsQueueFamily[]>(initialIds);
  // NAMING DISABLED: the two-stage "Add → type a name → Save" flow is commented
  // out below. Clicking "Add to Brand Kit" now saves immediately with the
  // family's base name as the preset label. Keep the state/handlers around for
  // easy re-enablement.
  // const [editing, setEditing] = useState<Record<string, string>>({});
  // Which row is currently saving.
  const [savingId, setSavingId] = useState<string | null>(null);

  // Reset when the dialog is (re)opened or when the input families change.
  // Note: we intentionally do NOT auto-close on queue.length===0 via an effect,
  // because on the open transition the queue state still holds the previous
  // render's value and would immediately close the dialog. Instead, callers
  // empty the queue through `removeFromQueue`, which closes explicitly when
  // the last row is removed.
  useEffect(() => {
    if (open) {
      setQueue(initialIds);
      // setEditing({});
      setSavingId(null);
    }
  }, [open, initialIds]);

  const removeFromQueue = (baseName: string) => {
    setQueue((prev) => {
      const next = prev.filter((f) => f.baseName !== baseName);
      if (next.length === 0) {
        // Defer so we don't call onClose while React is applying state.
        setTimeout(() => onClose(), 0);
      }
      return next;
    });
    // setEditing((prev) => {
    //   if (!(baseName in prev)) return prev;
    //   const next = { ...prev };
    //   delete next[baseName];
    //   return next;
    // });
  };

  // const handleAddClick = (family: BrandFontsQueueFamily) => {
  //   setEditing((prev) => ({ ...prev, [family.baseName]: family.baseName }));
  // };

  // const handleNameChange = (baseName: string, value: string) => {
  //   setEditing((prev) => ({ ...prev, [baseName]: value }));
  // };

  const handleSave = async (family: BrandFontsQueueFamily) => {
    if (savingId) return;
    // NAMING DISABLED: always use the family's base name as the preset label.
    // const name = (editing[family.baseName] ?? family.baseName).trim() || family.baseName;
    const name = family.baseName;
    const firstVariant = family.variantNames[0] || family.baseName;
    setSavingId(family.baseName);
    try {
      await onSave({
        label: name,
        fontFamily: family.baseName,
        fontVariant: firstVariant,
        fontSize: 16,
        lineHeight: 1,
        letterSpacing: 1,
      });
      removeFromQueue(family.baseName);
    } finally {
      setSavingId((current) => (current === family.baseName ? null : current));
    }
  };

  // Per-row "Upload" — the font is already uploaded by the time this dialog
  // opens, so this just drops the row from the queue without creating a
  // Brand Kit preset. The font stays available in the CDN / font picker.
  const handleUploadRow = (family: BrandFontsQueueFamily) => {
    if (savingId === family.baseName) return;
    removeFromQueue(family.baseName);
  };

  const handleDialogClose = () => {
    if (savingId) return;
    onClose();
  };

  // Footer "Cancel" — discards the entire remaining queue AND asks the
  // parent to delete the already-uploaded files (via onCancel).
  const [cancelling, setCancelling] = useState(false);
  const handleFooterCancel = async () => {
    if (savingId || cancelling) return;
    if (onCancel && queue.length > 0) {
      setCancelling(true);
      try {
        await onCancel(queue);
      } finally {
        setCancelling(false);
      }
    }
    onClose();
  };

  const remaining = queue.length;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      // Don't close when the user clicks outside the dialog — the queue has
      // real pending actions (Upload / Add to Brand Kit / Cancel) and an
      // accidental dismiss would leave uploaded fonts in a half-committed
      // state.
      disableBackdropClick
      disableEscapeKeyDown
      PaperProps={{
        style: {
          borderRadius: 8,
          padding: 16,
          minWidth: 360,
          maxWidth: 480,
        },
      }}
    >
      <div className={classes.root}>
        <div className={classes.header}>
          <p className={classes.title}>{title}</p>
          <p className={classes.subtitle}>
            {remaining === 1
              ? "1 font family detected. Add it to your Brand Kit or skip."
              : `${remaining} font families detected. Add each one to your Brand Kit or skip.`}
          </p>
        </div>

        <div className={classes.list}>
          {queue.map((family) => {
            const isSaving = savingId === family.baseName;
            const variantCount = family.variantNames.length;

            return (
              <div key={family.baseName} className={classes.row}>
                <div className={classes.rowTop}>
                  <div className={classes.familyCol}>
                    <span className={classes.familyName} title={family.baseName}>
                      {family.baseName}
                    </span>
                    <span className={classes.familyMeta}>
                      {variantCount === 1 ? "1 variant" : `${variantCount} variants`}
                    </span>
                  </div>
                  <div className={classes.actions}>
                    <button
                      type="button"
                      className={classes.cancelBtn}
                      onClick={() => handleUploadRow(family)}
                      disabled={isSaving || cancelling}
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      className={classes.primaryBtn}
                      onClick={() => handleSave(family)}
                      disabled={isSaving || cancelling}
                    >
                      {isSaving ? "Adding..." : "Add to Brand Kit"}
                    </button>
                  </div>
                </div>

                {/* NAMING DISABLED: the inline "Font name" input is commented out.
                    Clicking "Add to Brand Kit" now saves immediately using the
                    family's base name as the preset label.

                {isEditing && (
                  <div className={classes.editBlock}>
                    <div className={classes.field}>
                      <label className={classes.label}>Font name</label>
                      <input
                        className={classes.input}
                        type="text"
                        value={nameValue}
                        placeholder="Font name"
                        onChange={(e) => handleNameChange(family.baseName, e.target.value)}
                        autoFocus
                        disabled={isSaving}
                      />
                    </div>
                    <div className={classes.field}>
                      <label className={classes.label}>Font family</label>
                      <div className={classes.readonlyField}>{family.baseName}</div>
                    </div>
                    <div className={classes.actions}>
                      <button
                        type="button"
                        className={classes.cancelBtn}
                        onClick={() => handleCancelRow(family)}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className={classes.primaryBtn}
                        onClick={() => handleSave(family)}
                        disabled={isSaving || !nameValue.trim()}
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
                */}
              </div>
            );
          })}
        </div>

        <div className={classes.footer}>
          <button
            type="button"
            className={classes.cancelBtn}
            onClick={handleFooterCancel}
            disabled={!!savingId || cancelling}
          >
            {cancelling ? "Cancelling..." : "Cancel"}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  title: {
    color: "#0C172C",
    fontFamily: "Inter, sans-serif",
    fontSize: 16,
    fontWeight: 700,
    lineHeight: "24px",
    letterSpacing: "-0.312px",
    margin: 0,
  },
  subtitle: {
    color: "#798498",
    fontFamily: "Inter, sans-serif",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: "18px",
    margin: 0,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxHeight: 360,
    overflowY: "auto",
    paddingRight: 4,
  },
  row: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 10,
    border: "1px solid #E2E2FC",
    borderRadius: 6,
    background: "#FFFFFF",
  },
  rowTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  familyCol: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
  familyName: {
    color: "#0C172C",
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: "19px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  familyMeta: {
    color: "#798498",
    fontFamily: "Inter, sans-serif",
    fontSize: 11,
    fontWeight: 400,
    lineHeight: "14px",
  },
  editBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    paddingTop: 4,
    borderTop: "1px dashed #E2E2FC",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  label: {
    color: "#798498",
    fontFamily: "Inter, sans-serif",
    fontSize: 12,
    fontWeight: 500,
    lineHeight: "18px",
  },
  input: {
    height: 32,
    padding: "0 8px",
    fontSize: 12,
    border: "1px solid #E2E2FC",
    borderRadius: 4,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "Inter, sans-serif",
    color: "#2E3860",
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  readonlyField: {
    height: 32,
    padding: "0 8px",
    fontSize: 12,
    border: "1px solid #E2E2FC",
    borderRadius: 4,
    boxSizing: "border-box",
    fontFamily: "Inter, sans-serif",
    color: "#798498",
    background: "#F8F9FF",
    display: "flex",
    alignItems: "center",
    cursor: "default",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 4,
  },
  cancelBtn: {
    padding: "6px 12px",
    borderRadius: 4,
    border: "1px solid #E2E2FC",
    background: "white",
    color: "#2E3860",
    fontFamily: "Inter, sans-serif",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  primaryBtn: {
    padding: "6px 12px",
    borderRadius: 4,
    border: "none",
    background: "#EE4360",
    color: "white",
    fontFamily: "Inter, sans-serif",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
});

export default BrandFontsQueueDialog;
